import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { deploymentsApi } from '@/services/deployments.api'
import type { DeployLockStatus } from '@/types/deployment.types'

const POLL_LOCKED_MS = 10_000
const POLL_UNLOCKED_MS = 60_000

export const useDeployLockStore = defineStore('deployLock', () => {
  const locked = ref(false)
  const lockedBy = ref<string | null>(null)
  const lockedAt = ref<string | null>(null)
  const estimatedMs = ref<number | null>(null)
  const isLoading = ref(false)
  let pollingTimer: ReturnType<typeof setTimeout> | null = null

  const lockStatus = computed<DeployLockStatus>(() => ({
    locked: locked.value,
    lockedBy: lockedBy.value,
    lockedAt: lockedAt.value,
    estimatedMs: estimatedMs.value,
  }))

  const estimatedUnlockLabel = computed<string | null>(() => {
    if (!locked.value || !lockedAt.value || !estimatedMs.value) return null
    const unlock = new Date(new Date(lockedAt.value).getTime() + estimatedMs.value)
    return unlock.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  })

  function applyStatus(status: DeployLockStatus): void {
    locked.value = status.locked
    lockedBy.value = status.lockedBy
    lockedAt.value = status.lockedAt
    estimatedMs.value = status.estimatedMs
  }

  async function fetchLockStatus(): Promise<void> {
    isLoading.value = true
    try {
      const status = await deploymentsApi.getLockStatus()
      applyStatus(status)
    } catch (e) {
      console.error('[DeployLockStore] fetchLockStatus error:', e)
    } finally {
      isLoading.value = false
    }
  }

  function startPolling(): void {
    stopPolling()
    void fetchLockStatus()

    const schedule = () => {
      pollingTimer = setTimeout(async () => {
        await fetchLockStatus()
        schedule()
      }, locked.value ? POLL_LOCKED_MS : POLL_UNLOCKED_MS)
    }
    schedule()
  }

  function stopPolling(): void {
    if (pollingTimer) {
      clearTimeout(pollingTimer)
      pollingTimer = null
    }
  }

  return {
    locked,
    lockedBy,
    lockedAt,
    estimatedMs,
    isLoading,
    lockStatus,
    estimatedUnlockLabel,
    fetchLockStatus,
    startPolling,
    stopPolling,
  }
})
